import { ArrowUpRight, MessageCircle } from "lucide-react";
import { MaskedLines, Fade } from "./motion";
import { scrollToId } from "../lib/scroll";
import EnquiryForm from "./EnquiryForm";

const CONTACT = "vigneshkumarsekar@qrus.ai";
const PHONE_DISPLAY = "+91 91500 14806";
const PHONE_TEL = "tel:+919150014806";

const FOOTER_LINKS = [
    { label: "About", id: "about", testId: "footer-link-about" },
    { label: "Platform", id: "platform", testId: "footer-link-platform" },
    { label: "Student Journey", id: "journey", testId: "footer-link-journey" },
    { label: "Institutions", id: "institutions", testId: "footer-link-institutions" },
];

export default function FinalCTA() {
    return (
        <section id="contact" className="relative overflow-hidden bg-navy py-20 text-white md:py-40" data-testid="contact-section">
            <div className="pointer-events-none absolute -right-48 top-1/2 hidden h-[760px] w-[760px] -translate-y-1/2 rounded-full border border-sky/15 lg:block" />
            <div className="pointer-events-none absolute -right-24 top-1/2 hidden h-[520px] w-[520px] -translate-y-1/2 rounded-full border border-white/5 lg:block" />
            <div className="blueprint-grid-dark pointer-events-none absolute inset-0 opacity-60" />

            <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
                <Fade y={16}>
                    <div className="flex items-center gap-4 md:gap-6">
                        <span className="font-display text-sm font-semibold tracking-[0.22em] text-sky">CH.08</span>
                        <span className="h-px flex-1 bg-white/15" />
                        <span className="text-[11px] uppercase tracking-[0.3em] text-sky/70 md:text-xs">Begin</span>
                    </div>
                </Fade>

                <div className="mt-16 md:mt-24">
                    <MaskedLines
                        stagger={0.16}
                        lineClassName="font-display text-[clamp(2.9rem,12vw,10rem)] font-bold leading-[0.98] tracking-[-0.02em] text-white"
                        lines={[
                            <span key="a">
                                Stay <span className="text-sky">curious.</span>
                            </span>,
                        ]}
                    />
                    <Fade delay={0.35} className="mt-10 max-w-xl">
                        <p className="text-lg leading-relaxed text-sky/80 md:text-xl">
                            Build the engineers industry is waiting for.
                        </p>
                    </Fade>
                </div>

                <Fade delay={0.45} className="mt-14 flex flex-wrap items-center gap-4">
                    <a
                        data-testid="cta-institutions"
                        href={`mailto:${CONTACT}?subject=For%20Institutions%20—%20QRUS`}
                        className="group flex items-center gap-2.5 bg-sky px-8 py-4 text-sm font-medium tracking-wide text-navy transition-colors duration-300 hover:bg-white"
                    >
                        For Institutions
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <a
                        data-testid="cta-students"
                        href={`mailto:${CONTACT}?subject=For%20Students%20—%20QRUS`}
                        className="group flex items-center gap-2.5 border border-white/30 px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:border-sky hover:text-sky"
                    >
                        For Students
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                </Fade>

                <div className="mt-24 grid gap-14 border-t border-white/15 pt-12 md:mt-32 lg:grid-cols-12 lg:gap-10">
                    <Fade delay={0.5} className="lg:col-span-7">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-slate">
                            Partnerships &amp; Enquiries
                        </span>
                        <EnquiryForm />
                    </Fade>
                    <Fade delay={0.6} className="lg:col-span-5">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-slate">
                            Direct
                        </span>
                        <div className="mt-8 space-y-9">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-sky/50">Email</p>
                                <a
                                    data-testid="contact-email-link"
                                    href={`mailto:${CONTACT}`}
                                    className="group mt-2.5 flex flex-wrap items-center gap-3 break-all font-display text-lg font-medium tracking-tight text-white transition-colors duration-300 hover:text-sky md:text-xl"
                                >
                                    {CONTACT}
                                    <ArrowUpRight className="h-5 w-5 shrink-0 text-sky transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-sky/50">Phone</p>
                                <a
                                    data-testid="contact-phone-link"
                                    href={PHONE_TEL}
                                    className="mt-2.5 block w-fit font-display text-lg font-medium tracking-tight text-white transition-colors duration-300 hover:text-sky md:text-xl"
                                >
                                    {PHONE_DISPLAY}
                                </a>
                                <a
                                    data-testid="contact-whatsapp-link"
                                    href="https://wa.me/919150014806"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2.5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-sky transition-colors duration-300 hover:text-white"
                                >
                                    <MessageCircle className="h-3.5 w-3.5" />
                                    Chat on WhatsApp
                                </a>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-sky/50">Address</p>
                                <p data-testid="contact-address" className="mt-2.5 max-w-xs text-sm leading-relaxed text-sky/70">
                                    Qrus Innovation, Vasantham Nagar, Parktown,<br />
                                    Keelpanangadi, Madurai — 625007
                                </p>
                            </div>
                        </div>
                    </Fade>
                </div>

                <footer className="mt-20 border-t border-white/10 pt-8 md:mt-24">
                    <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
                        <button
                            data-testid="footer-logo"
                            onClick={() => scrollToId("top")}
                            className="flex items-baseline gap-1.5 text-left"
                            aria-label="QRUS — back to top"
                        >
                            <span className="font-display text-lg font-bold tracking-tight text-white">QRUS</span>
                            <span className="h-1.5 w-1.5 bg-sky" />
                        </button>
                        <nav className="flex flex-wrap items-center gap-x-7 gap-y-3" aria-label="Footer">
                            {FOOTER_LINKS.map((link) => (
                                <button
                                    key={link.id}
                                    data-testid={link.testId}
                                    onClick={() => scrollToId(link.id)}
                                    className="text-xs font-medium tracking-wide text-slate transition-colors duration-300 hover:text-sky"
                                >
                                    {link.label}
                                </button>
                            ))}
                            <a
                                data-testid="footer-signin-link"
                                href="https://qrus.ai/login"
                                className="text-xs font-medium tracking-wide text-sky/80 transition-colors duration-300 hover:text-sky"
                            >
                                Sign in
                            </a>
                        </nav>
                        <p className="text-xs tracking-wide text-slate">
                            Pronounced &lsquo;Curious&rsquo;
                        </p>
                    </div>
                    <div className="mt-6 flex flex-col gap-3 border-t border-white/5 pt-6 text-xs leading-relaxed text-slate md:flex-row md:items-center md:justify-between">
                        <p data-testid="footer-copyright">© 2026 QRUS Innovation. All rights reserved.</p>
                        <a
                            data-testid="footer-whatsapp-link"
                            href="https://wa.me/919150014806"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex w-fit items-center gap-2 border border-sky/40 px-4 py-2 text-xs font-medium tracking-wide text-sky transition-colors duration-300 hover:bg-sky hover:text-navy"
                        >
                            <MessageCircle className="h-3.5 w-3.5" />
                            WhatsApp {PHONE_DISPLAY}
                        </a>
                    </div>
                </footer>
            </div>
        </section>
    );
}
